// Phase 92C — Player Development Report builder constants.
// Section keys MUST mirror backend development_report_models.SECTION_KEYS.

export const SECTION_KEYS = [
  'overview', 'football_performance', 'match_analysis', 'physical_gps',
  'fitness', 'biometrics', 'attendance', 'mental', 'pdp', 'injury_rehab',
  'development_summary',
];

export const SECTION_LABELS = {
  overview: 'Player Development Overview',
  development_summary: 'Development Summary & Priorities',
  football_performance: 'Football Performance',
  match_analysis: 'Match Analysis & Video Evidence',
  physical_gps: 'Physical / GPS',
  fitness: 'Fitness',
  biometrics: 'Biometrics',
  attendance: 'Attendance & Availability',
  mental: 'Mental Development',
  pdp: 'Player Development Plan',
  injury_rehab: 'Injury / Rehabilitation',
  custom: 'Custom Sections',
};

export const SECTION_SHORT = {
  overview: 'Overview',
  development_summary: 'Summary & Priorities',
  football_performance: 'Football',
  match_analysis: 'Match Analysis',
  physical_gps: 'Physical / GPS',
  fitness: 'Fitness',
  biometrics: 'Biometrics',
  attendance: 'Attendance',
  mental: 'Mental',
  pdp: 'PDP',
  injury_rehab: 'Injury / Rehab',
  custom: 'Custom Sections',
};

// Order sections appear in the BUILDER navigation (editing order).
export const BUILDER_ORDER = [
  'overview', 'football_performance', 'match_analysis', 'physical_gps',
  'fitness', 'biometrics', 'attendance', 'mental', 'pdp', 'injury_rehab',
  'development_summary',
];

// Order sections appear in the PLAYER/PARENT preview (development-first).
export const PREVIEW_ORDER = [
  'overview', 'development_summary', 'football_performance', 'match_analysis',
  'physical_gps', 'fitness', 'biometrics', 'attendance', 'mental', 'pdp',
  'injury_rehab',
];

// Backend benchmark comparison status → human label + tone.
export const TARGET_STATUS = {
  above_target: { label: 'Above Development Target', tone: 'positive' },
  within_target: { label: 'Within Development Target Zone', tone: 'positive' },
  approaching_target: { label: 'Approaching Development Target', tone: 'progress' },
  development_priority: { label: 'Development Priority', tone: 'priority' },
  insufficient_data: { label: 'Insufficient Data', tone: 'neutral' },
  context_only: { label: 'For Context', tone: 'neutral' },
};

export const isSensitiveSection = (k) => ['mental', 'pdp', 'injury_rehab'].includes(k);

// Mirror of backend development_report_models.PDR_SECTION_SOURCE_MODULE — used
// ONLY to disable sections the staff user cannot source (§8). Backend stays
// authoritative; this is a UX affordance. null => report-owned (no gating).
export const SECTION_SOURCE_MODULE = {
  overview: null,
  football_performance: 'matches',
  match_analysis: 'matches',
  physical_gps: 'matches',
  fitness: 'fitness_assessment',
  biometrics: 'biometrics',
  attendance: 'attendance',
  mental: 'mental_hub',
  pdp: 'pdp',
  injury_rehab: 'injury_recovery',
  development_summary: null,
};

// Review-period shortcuts → resolved {start, end} (ISO yyyy-mm-dd) as of `today`.
export function resolvePeriod(shortcut, today = new Date(), seasonStart = null) {
  const iso = (d) => d.toISOString().slice(0, 10);
  const end = new Date(today);
  const start = new Date(today);
  switch (shortcut) {
    case 'last_month':
      start.setMonth(start.getMonth() - 1);
      return { start: iso(start), end: iso(end) };
    case 'last_3_months':
      start.setMonth(start.getMonth() - 3);
      return { start: iso(start), end: iso(end) };
    case 'season_to_date':
      return { start: seasonStart || iso(new Date(end.getFullYear(), 0, 1)), end: iso(end) };
    default:
      return { start: iso(start), end: iso(end) };
  }
}

export const fmtDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return String(v); }
};

export const fmtDateTime = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(v); }
};

// null = not recorded (show —); 0 is a real measured value.
export const fmtNum = (v, digits = null) => {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number') return String(v);
  return digits === null ? String(v) : v.toFixed(digits);
};
